import { DurableIdbCache } from './durable-idb-cache.ts';

export { DurableIdbCache };

export type DurableCacheValidator<P> = (key: string, value: P) => boolean;

export type DurableCacheConfig<P> = {
  cacheName: string;
  validator?: DurableCacheValidator<P>;
  loadFromPersistentCache?: boolean;
};

export class DurableCache<P> {
  // basic per-session cache
  private cache = new Map<string, P>();
  idbCache: DurableIdbCache<P>;
  loadFromPersistentCache: boolean;

  constructor(config: DurableCacheConfig<P>) {
    this.idbCache = new DurableIdbCache<P>(config);
    this.loadFromPersistentCache = config.loadFromPersistentCache ?? true;
  }

  public user() {
    return this.idbCache.user();
  }

  public async prepare(currentUser: string) {
    await this.idbCache.prepare();

    // if the user has changed, or load from loadFromPersistentCache = false,
    // then dont load data from idb, and clean in-memory cache
    const lastUser = this.idbCache.user();
    const isSameUser = lastUser === currentUser;
    if (!isSameUser || !this.loadFromPersistentCache) {
      await this.idbCache.cleanup(currentUser);
      return;
    }

    // else, populate cache with existing data
    const entries = await this.idbCache.entries();
    entries.forEach(([key, value]) => {
      this.cache.set(key as string, value);
    });
  }

  public get(key: string) {
    return this.cache.get(key);
  }

  public set(key: string, data: P) {
    this.cache.set(key, data);
    // we do not wait for the IndexedDB commit. This can finish async
    // at any point in time without affecting user experience.
    this.idbCache.set(key, data);
  }

  public entries() {
    return this.idbCache.entries() ?? [];
  }

  public delete(key: string) {
    this.cache.delete(key);
    this.idbCache.delete(key);
  }
}
